import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Loader } from "@/components/common/Bits";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  available: "text-emerald-600 bg-emerald-50",
  occupied: "text-orange-600 bg-orange-50",
  reserved: "text-violet-600 bg-violet-50",
  billing: "text-sky-600 bg-sky-50",
  cleaning: "text-stone-500 bg-stone-100",
};

const formatTime12Hour = (time) => {
  if (!time) return "";

  const [hours, minutes] = time.split(":").map(Number);

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function TableDetail() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { branchId, branches } = useApp();

  const activeBranch = branchId === "all" ? branches[0]?.id : branchId;

  const [table, setTable] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);

  useEffect(() => {
    if (!activeBranch) return;

    api
      .tables(activeBranch)
      .then((r) => {
        const found = r.data.find((t) => t.id === tableId);
        setTable(found || null);
      })
      .catch(() => setTable(null));

    api
      .reservations(activeBranch)
      .then((r) => {
        setReservations(
          r.data.filter(
            (reservation) =>
              reservation.table_id === tableId &&
              reservation.status !== "cancelled" &&
              reservation.status !== "no_show",
          ),
        );
      })
      .catch(() => setReservations([]));

    api
      .orders({
        branch_id: activeBranch,
        type: "dine_in",
        limit: 200,
      })
      .then((r) => {
        const order = r.data.find(
          (o) =>
            o.table_id === tableId &&
            ["placed", "preparing", "ready"].includes(o.status) &&
            o.payment_status !== "paid",
        );

        setCurrentOrder(order || null);
      })
      .catch(() => setCurrentOrder(null));
  }, [activeBranch, tableId]);

  if (!table) {
    return (
      <div className="p-6">
        <Loader label="Loading table" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <Button
        variant="outline"
        className="rounded-xl mb-5"
        onClick={() => navigate("/app/tables")}
      >
        <ArrowLeft size={16} className="mr-2" />
        Back to Tables
      </Button>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">{table.name}</h1>

            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2">
              <Users size={15} />
              {table.seats} seats
            </div>
          </div>

          <span
            className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold capitalize",
              STATUS_STYLE[table.status] || STATUS_STYLE.available,
            )}
          >
            {table.status}
          </span>
        </div>

        {currentOrder && (
          <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-orange-700">
                  Current Order
                </p>

                <p className="text-sm font-bold text-foreground mt-1">
                  Order #{currentOrder.order_no}
                </p>
              </div>

              <span className="font-bold text-orange-700">
                ₹{Number(currentOrder.total || 0).toFixed(2)}
              </span>
            </div>

            <div className="mt-3 space-y-1">
              {(currentOrder.items || []).slice(0, 3).map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-muted-foreground">{item.name}</span>

                  <span className="font-medium">× {item.qty}</span>
                </div>
              ))}

              {(currentOrder.items || []).length > 3 && (
                <p className="text-xs text-muted-foreground pt-1">
                  + {(currentOrder.items || []).length - 3} more items
                </p>
              )}
            </div>

            <Button
              className="mt-4 rounded-xl"
              onClick={() =>
                navigate("/app/pos", {
                  state: {
                    existingOrder: currentOrder,
                    addItemMode: true,
                  },
                })
              }
            >
              View / Add Items
            </Button>
          </div>
        )}

        {reservations.length > 0 && (
          <div className="mt-6">
            <h2 className="font-semibold">Reservations</h2>

            <div className="mt-3 space-y-2">
              {reservations.map((reservation) => (
                <div
                  key={reservation.id}
                  className="rounded-xl border border-border p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {reservation.customer_name}
                    </span>

                    <span className="text-sm text-muted-foreground">
                      {formatTime12Hour(reservation.time)}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mt-1">
                    {reservation.date} · {reservation.guests} guests
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {table.status === "available" && (
          <Button
            className="mt-6 rounded-xl"
            onClick={() =>
              navigate("/app/pos", {
                state: {
                  tableId: table.id,
                },
              })
            }
          >
            Open POS for {table.name}
          </Button>
        )}

        {reservations.length === 0 && (
          <p className="text-sm text-muted-foreground mt-6">
            No active reservations for this table.
          </p>
        )}
      </div>
    </div>
  );
}
